/**
 * GatewayRouterSettingsCard (OCP-882) - the router-wide failover settings of
 * the managed-AI gateway: what a deployment inherits when it has no `timeout` /
 * `num_retries` of its own, and how long a failing deployment is skipped.
 *
 * On 2026-10-16 the gateway ran num_retries 2, timeout 6000 s and cooldown_time
 * 5 s; editing them needed an SSH session on the gateway host. LiteLLM takes
 * them at runtime (POST /config/update), so they are an OCP surface now (REST
 * /v1/admin/gateway/router-settings, MCP ocp_gateway_router_settings /
 * ocp_set_gateway_router_settings, audited with before / after).
 *
 * Self-fetching like the other gateway cards, so a failed read never blanks
 * the model list. Saving sends only the fields that changed (merge).
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';

interface RouterSettings {
  routing_strategy: string | null;
  /** OCP-941 - latency-routing knobs (`ttl`, `lowest_latency_buffer`); null = not reported. */
  routing_strategy_args?: Record<string, unknown> | null;
  num_retries: number | null;
  timeout: number | null;
  allowed_fails: number | null;
  cooldown_time: number | null;
  retry_after: number | null;
}

interface Bounds {
  num_retries: { min: number; max: number };
  timeout: { min: number; max: number };
  allowed_fails: { min: number; max: number };
  cooldown_time: { min: number; max: number };
  retry_after: { min: number; max: number };
}

interface RouterSettingsResponse {
  gateway_configured: boolean;
  settings: RouterSettings | null;
  recommended: { num_retries: number; timeout: number; cooldown_time: number; allowed_fails: number };
  bounds: Bounds;
}

type Key = 'num_retries' | 'timeout' | 'allowed_fails' | 'cooldown_time' | 'retry_after';

const FIELDS: Array<{ key: Key; label: string; hint: string }> = [
  { key: 'num_retries', label: 'Retries', hint: 'router-wide, for a deployment without its own retries' },
  { key: 'timeout', label: 'Timeout (s)', hint: 'router-wide, for a deployment without its own timeout' },
  { key: 'allowed_fails', label: 'Allowed fails', hint: 'must not be 3 (LiteLLM default = no effect)' },
  { key: 'cooldown_time', label: 'Cooldown (s)', hint: 'a cooled-down deployment is skipped this long' },
  { key: 'retry_after', label: 'Retry after (s)', hint: 'minimum wait between retries' },
];

/** OCP-941 - the strategies the API accepts; the strategy picks only within one order tier. */
const STRATEGIES = ['simple-shuffle', 'latency-based-routing', 'least-busy', 'usage-based-routing-v2'] as const;

interface StrategyForm { strategy: string; ttl: string; buffer: string }

function toStrategyForm(s: RouterSettings | null): StrategyForm {
  const args = s?.routing_strategy_args ?? null;
  const v = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? String(x) : '');
  return { strategy: s?.routing_strategy ?? '', ttl: v(args?.ttl), buffer: v(args?.lowest_latency_buffer) };
}

function toForm(s: RouterSettings | null): Record<Key, string> {
  const v = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));
  return {
    num_retries: v(s?.num_retries),
    timeout: v(s?.timeout),
    allowed_fails: v(s?.allowed_fails),
    cooldown_time: v(s?.cooldown_time),
    retry_after: v(s?.retry_after),
  };
}

export function GatewayRouterSettingsCard() {
  const [data, setData] = useState<RouterSettingsResponse | null>(null);
  const [form, setForm] = useState<Record<Key, string>>(toForm(null));
  const [strategyForm, setStrategyForm] = useState<StrategyForm>(toStrategyForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await api<RouterSettingsResponse>('/v1/admin/gateway/router-settings');
      setData(res);
      setForm(toForm(res.settings));
      setStrategyForm(toStrategyForm(res.settings));
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? `Could not load router settings (HTTP ${err.status}).` : 'Could not load router settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const current = data?.settings ?? null;
  const changed = (Object.keys(form) as Key[]).filter((k) => {
    const raw = form[k].trim();
    return raw !== '' && current !== null && Number(raw) !== current[k];
  });

  const save = useCallback(
    async (values: Record<string, unknown>) => {
      setSaving(true);
      setMessage(null);
      try {
        const res = await api<{ settings: RouterSettings; verified: boolean; notice?: string }>('/v1/admin/gateway/router-settings', { method: 'PATCH', body: values });
        setData((d) => (d ? { ...d, settings: res.settings } : d));
        setForm(toForm(res.settings));
        setStrategyForm(toStrategyForm(res.settings));
        setMessage(
          res.verified
            ? { kind: 'ok', text: 'Saved and read back from the gateway. Other gateway workers pick it up within about 30 seconds.' }
            : { kind: 'error', text: res.notice ?? 'Saved, but the gateway read-back does not show every value yet - refresh in a minute.' },
        );
      } catch (err) {
        setMessage({ kind: 'error', text: err instanceof ApiError ? (err.status === 400 ? 'Validation failed - check the bounds.' : `Save failed (HTTP ${err.status}).`) : 'Save failed.' });
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const saveChanged = () => {
    const values: Partial<Record<Key, number>> = {};
    for (const k of changed) values[k] = Number(form[k]);
    void save(values);
  };

  // OCP-941 - strategy and its args are saved together. LiteLLM applies the args only when the
  // strategy changes to a different value (or the gateway restarts); an args change on the live
  // strategy is stored and the API answers verified false with a notice, shown below.
  const savedStrategy = toStrategyForm(current);
  const strategyChanged =
    strategyForm.strategy !== '' && current !== null
    && (strategyForm.strategy !== savedStrategy.strategy || strategyForm.ttl.trim() !== savedStrategy.ttl || strategyForm.buffer.trim() !== savedStrategy.buffer);
  const saveStrategy = () => {
    const args: Record<string, number> = {};
    if (strategyForm.ttl.trim() !== '') args.ttl = Number(strategyForm.ttl);
    if (strategyForm.buffer.trim() !== '') args.lowest_latency_buffer = Number(strategyForm.buffer);
    void save({ routing_strategy: strategyForm.strategy, ...(Object.keys(args).length ? { routing_strategy_args: args } : {}) });
  };

  const rec = data?.recommended ?? null;
  const differsFromRecommended =
    rec !== null && current !== null && (current.num_retries !== rec.num_retries || current.timeout !== rec.timeout || current.cooldown_time !== rec.cooldown_time || current.allowed_fails !== rec.allowed_fails);

  return (
    <section data-testid="gateway-router-settings-card" className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Router failover settings</h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            Gateway-wide defaults for every deployment that has no timeout or retries of its own, and how long a
            failing deployment is skipped. A deployment&apos;s own values always win. Changes apply live (no restart)
            and are audited. Once saved here they are stored in the gateway database together with its model
            alias map: editing these keys or <span className="font-mono">model_group_alias</span> in litellm-config.yaml no longer has any effect.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading || saving}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {message && (
        <div
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={`mt-4 rounded border px-3 py-2 text-sm ${
            message.kind === 'error'
              ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
              : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300'
          }`}
        >
          {message.text}
        </div>
      )}

      {data && !data.gateway_configured && (
        <p className="mt-4 text-sm text-gray-500">No gateway URL or master key set - there are no router settings to tune.</p>
      )}

      {data && data.gateway_configured && current && (
        <>
          {differsFromRecommended && rec && (
            <p data-testid="router-settings-recommendation" className="mt-4 text-xs text-amber-800 dark:text-amber-300">
              Recommended: retries {rec.num_retries}, timeout {rec.timeout} s, cooldown {rec.cooldown_time} s, allowed fails {rec.allowed_fails}.
              With the gateway defaults (retries 2, timeout 6000 s, cooldown 5 s) a deployment without its own values keeps a stall alive
              for minutes and is tried again 5 seconds later.
            </p>
          )}
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {FIELDS.map((f) => (
              <label key={f.key} className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{f.label}</span>
                <input
                  value={form[f.key]}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  inputMode="numeric"
                  data-testid={`router-setting-${f.key}`}
                  className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <span className="mt-0.5 block text-xs text-gray-400">{f.hint}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4" data-testid="router-strategy">
            <label className="block sm:col-span-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Routing strategy</span>
              <select
                value={strategyForm.strategy}
                onChange={(e) => setStrategyForm((f) => ({ ...f, strategy: e.target.value }))}
                data-testid="router-setting-routing_strategy"
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:outline-none"
              >
                {strategyForm.strategy !== '' && !(STRATEGIES as readonly string[]).includes(strategyForm.strategy) && (
                  <option value={strategyForm.strategy}>{strategyForm.strategy}</option>
                )}
                {STRATEGIES.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
              <span className="mt-0.5 block text-xs text-gray-400">picks among deployments of the same order tier; order always decides first. Latency args apply on a change of strategy or a gateway restart; stored args cannot be cleared from here.</span>
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Latency TTL (s)</span>
              <input
                value={strategyForm.ttl}
                onChange={(e) => setStrategyForm((f) => ({ ...f, ttl: e.target.value }))}
                inputMode="numeric"
                data-testid="router-setting-ttl"
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:outline-none"
              />
              <span className="mt-0.5 block text-xs text-gray-400">latency history per deployment</span>
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Latency buffer</span>
              <input
                value={strategyForm.buffer}
                onChange={(e) => setStrategyForm((f) => ({ ...f, buffer: e.target.value }))}
                inputMode="decimal"
                data-testid="router-setting-buffer"
                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-brand-500 focus:outline-none"
              />
              <span className="mt-0.5 block text-xs text-gray-400">0..1 share around the fastest</span>
            </label>
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={saveStrategy}
              disabled={saving || !strategyChanged}
              data-testid="router-strategy-save"
              className="rounded border border-gray-300 dark:border-gray-700 px-4 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save routing strategy
            </button>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={saveChanged}
              disabled={saving || changed.length === 0}
              className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={() => rec && void save({ num_retries: rec.num_retries, timeout: rec.timeout, cooldown_time: rec.cooldown_time, allowed_fails: rec.allowed_fails })}
              disabled={saving || !differsFromRecommended}
              className="rounded border border-gray-300 dark:border-gray-700 px-4 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              Apply recommended
            </button>
          </div>
        </>
      )}
    </section>
  );
}
